import React from 'react';
import { NavTab } from '../types';

// Default navigation tabs
export const DEFAULT_TABS: NavTab[] = [
  { id: 'map', label: 'Map', icon: 'map' },
  { id: 'contribute', label: 'Contribute', icon: 'volunteer_activism' },
  { id: 'settings', label: 'Settings', icon: 'settings' },
];

interface BottomNavProps {
  tabs?: NavTab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
}

export const BottomNav: React.FC<BottomNavProps> = ({
  tabs = DEFAULT_TABS,
  activeTab,
  onTabChange
}) => {
  return (
    <nav className="flex justify-around items-center bg-white border-t border-gray-200 pb-safe-bottom pt-1">
      {tabs.map((tab) => (
        <NavButton
          key={tab.id}
          icon={tab.icon}
          label={tab.label}
          active={activeTab === tab.id}
          onClick={() => onTabChange(tab.id)}
        />
      ))}
    </nav>
  );
};

interface NavButtonProps {
  icon: string;
  label: string;
  active?: boolean;
  onClick?: () => void;
  badge?: number;
}

export const NavButton: React.FC<NavButtonProps> = ({
  icon,
  label,
  active = false,
  onClick,
  badge
}) => {
  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center justify-center w-full py-2 transition-colors touch-feedback ${
        active ? 'text-blue-600' : 'text-gray-500'
      }`}
    >
      <div className="relative">
        <span
          className={`material-symbols-outlined text-[24px] mb-0.5 ${active ? 'filled' : ''}`}
          style={active ? { fontVariationSettings: "'FILL' 1" } : {}}
        >
          {icon}
        </span>
        {badge !== undefined && badge > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </div>
      <span className="text-[10px] font-medium">{label}</span>
    </button>
  );
};

// Floating Action Button component
interface FABProps {
  icon: string;
  onClick: () => void;
  label?: string;
  position?: 'left' | 'center' | 'right';
}

export const FAB: React.FC<FABProps> = ({
  icon,
  onClick,
  label,
  position = 'right'
}) => {
  const positionClasses = {
    left: 'left-4',
    center: 'left-1/2 -translate-x-1/2',
    right: 'right-4'
  };

  return (
    <button
      onClick={onClick}
      className={`fixed bottom-24 ${positionClasses[position]} bg-blue-600 text-white rounded-full shadow-lg active:bg-blue-700 transition-all touch-feedback ${
        label ? 'px-5 py-3' : 'p-4'
      }`}
    >
      <div className="flex items-center space-x-2">
        <span className="material-symbols-outlined text-[24px]">{icon}</span>
        {label && <span className="font-medium">{label}</span>}
      </div>
    </button>
  );
};
